/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file exports the Form component family alongside the
// `useFormField` hook per the shadcn/ui convention. Hooks and
// components share a file here by design — the hook is only useful
// inside the FormField provider chain. Fast Refresh needs a
// component-only file, so this fallback to full-page reload is
// intentional and out of scope for the lint-cleanup pass.
/* eslint-disable react-refresh/only-export-components */
'use client';

import { createContext, useContext, useId, type ComponentProps } from 'react';
import type { Root as LabelPrimitiveRoot } from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
    Controller,
    FormProvider,
    useFormContext,
    useFormState,
    type ControllerProps,
    type FieldPath,
    type FieldValues,
} from 'react-hook-form';

import { Label } from '@/components/forms/label';
import { cn } from '@/lib/utils';

const Form = FormProvider;

type FormFieldContextValue<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
    name: TName;
};

const FormFieldContext = createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    ...props
}: ControllerProps<TFieldValues, TName>) => {
    return (
        <FormFieldContext.Provider value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext.Provider>
    );
};

type FormItemContextValue = {
    id: string;
};

const FormItemContext = createContext<FormItemContextValue>({} as FormItemContextValue);

const useFormField = () => {
    const fieldContext = useContext(FormFieldContext);
    const itemContext = useContext(FormItemContext);

    const { getFieldState } = useFormContext();
    const formState = useFormState({ name: fieldContext.name });
    const fieldState = getFieldState(fieldContext.name, formState);
    const { id } = itemContext;

    return {
        id,
        name: fieldContext.name,
        formItemId: `${id}-form-item`,
        formDescriptionId: `${id}-form-item-description`,
        formMessageId: `${id}-form-item-message`,
        ...fieldState,
    };
};

function FormItem({ className, ...props }: ComponentProps<'div'>) {
    const id = useId();

    return (
        <FormItemContext.Provider value={{ id }}>
            <div
                data-slot="form-item"
                className={cn(
                    'grid gap-2 has-[[data-form-control][disabled]]:opacity-50 has-[[data-form-control][data-disabled]]:opacity-50',
                    className
                )}
                {...props}
            />
        </FormItemContext.Provider>
    );
}

function FormLabel({ className, ...props }: ComponentProps<typeof LabelPrimitiveRoot>) {
    const { error, formItemId } = useFormField();

    return (
        <Label
            data-slot="form-label"
            className={cn(error && 'text-destructive', className)}
            htmlFor={formItemId}
            {...props}
        />
    );
}

function FormControl({ ...props }: ComponentProps<typeof Slot>) {
    const { error, formItemId, formMessageId } = useFormField();

    return (
        <Slot
            data-form-control=""
            data-slot="form-control"
            id={formItemId}
            aria-describedby={error ? formMessageId : undefined}
            aria-invalid={!!error}
            {...props}
        />
    );
}

function FormDescription({ className, ...props }: ComponentProps<'p'>) {
    const { formDescriptionId } = useFormField();

    return (
        <p
            data-slot="form-description"
            id={formDescriptionId}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    );
}

function FormMessage({ className, ...props }: ComponentProps<'p'>) {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message ?? '') : props.children;

    if (!body) {
        return null;
    }

    return (
        <p data-slot="form-message" id={formMessageId} className={cn('text-xs text-destructive', className)} {...props}>
            {body}
        </p>
    );
}

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
